import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SecureAuth } from "asset/icons";
import ErrorMessage from "../ErrorMessage";
import Loader from "component/loader";
import { COMPLETE_LOGIN } from "store/action";
import { checkAutoLogin } from "store/authServices";

const BackupVerificationModal = ({
    isOpen,
    onClose,
    onSuccess,
    onBack,
    onVerifyCode,
    title = "Enter backup verification code",
    description = "Provide your 12-digit backup verification code given to you when you set up your 2FA.",
    placeholder = "Backup verification code",
    helpText = "Need help? Contact support",
    backLabel = "Go back",
    submitLabel = "Verify",
    emptyMessage = "Please enter your backup verification code.",
    errorFallback = "Invalid backup verification code. Please try again.",
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [backupCode, setBackupCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        setBackupCode("");
        setError("");

        if (typeof onVerifyCode === "function") return;

        const pendingLogin = sessionStorage.getItem("pendingLogin");
        if (!pendingLogin) {
            navigate("/auth/signin", { replace: true });
        }
    }, [isOpen, navigate, onVerifyCode]);

    if (!isOpen) return null;

    const finishLogin = async (verifiedLoginData) => {
        COMPLETE_LOGIN(verifiedLoginData, dispatch);
        sessionStorage.removeItem("pendingLogin");
        await checkAutoLogin(dispatch);

        if (typeof onSuccess === "function") {
            onSuccess(verifiedLoginData);
            return;
        }

        if (verifiedLoginData?.user?.pinStatus === "NOT_VERIFIED") {
            navigate("/auth/setup-pin", { replace: true });
            return;
        }

        const lastVisitedPath = sessionStorage.getItem("lastVisitedPath");
        if (
            lastVisitedPath &&
            lastVisitedPath !== "/" &&
            lastVisitedPath !== "/auth/signin"
        ) {
            sessionStorage.removeItem("lastVisitedPath");
            navigate(lastVisitedPath, { replace: true });
            return;
        }

        navigate("/dashboard", { replace: true });
    };

    const handleVerify = async () => {
        if (!backupCode.trim()) {
            setError(emptyMessage);
            return;
        }

        try {
            setLoading(true);
            setError("");

            if (typeof onVerifyCode === "function") {
                const result = await onVerifyCode(backupCode.trim());
                setBackupCode("");

                if (typeof onSuccess === "function") {
                    onSuccess(result);
                } else if (typeof onClose === "function") {
                    onClose();
                }

                return;
            }

            const pendingLoginString = sessionStorage.getItem("pendingLogin");
            if (!pendingLoginString) {
                setError("Your sign-in session expired. Please sign in again.");
                navigate("/auth/signin", { replace: true });
                return;
            }

            const pendingLogin = JSON.parse(pendingLoginString);

            const verifyResponse = await axios.post(
                "/v1/users/mfa-backup-code",
                { code: backupCode.trim() },
                {
                    headers: {
                        Authorization: `Bearer ${pendingLogin.token}`,
                    },
                }
            );

            const verifiedLoginData = verifyResponse?.data?.data || {};
            if (!verifiedLoginData?.token) {
                throw new Error("Missing permanent token from backup verification response.");
            }

            await finishLogin(verifiedLoginData);
        } catch (verifyError) {
            setError(
                verifyError?.response?.data?.message ||
                    verifyError?.message ||
                    errorFallback
            );
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (typeof onBack === "function") {
            onBack();
            return;
        }

        if (typeof onClose === "function") {
            onClose();
        }
        navigate("/auth/signin/confirm-2fa");
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 py-8">
            <div className="bg-white rounded-xl w-full max-w-[650px] mx-auto border border-[#EBEDF9] relative">
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-4 top-4 text-xl text-[#667085]"
                    >
                        ×
                    </button>
                )}

                <div className="!pt-5 pl-2">
                    <SecureAuth />
                </div>

                <div className="px-8 pb-8">
                    <h2 className="text-2xl font-bold text-[#101828]">
                        {title}
                    </h2>
                    <p className="mt-2 text-sm leading-7 text-[#667085]">
                        {description}
                    </p>

                    <div className="mt-8">
                        <input
                            type="text"
                            value={backupCode}
                            onChange={(event) => {
                                setBackupCode(event.target.value);
                                setError("");
                            }}
                            placeholder={placeholder}
                            className="h-16 w-full rounded-xl bg-[#F2F4F7] px-6 text-[#344054] outline-none transition placeholder:text-[#667085] focus:border-[#6F00FF] focus:ring-2 focus:ring-[#E9D7FE]"
                        />
                    </div>

                    <div className="mt-4">
                        <ErrorMessage message={error} />
                    </div>

                    <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <button
                            type="button"
                            className="text-left text-sm font-medium text-[#6F00FF] hover:underline"
                        >
                            {helpText}
                        </button>

                        <div className="flex items-center gap-6">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="text-sm font-medium text-[#101828] hover:underline"
                            >
                                {backLabel}
                            </button>

                            <button
                                type="button"
                                onClick={handleVerify}
                                disabled={!backupCode.trim() || loading}
                                className={`rounded-lg bg-[#6F00FF] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#6f00fff0] ${
                                    !backupCode.trim() || loading
                                        ? "cursor-not-allowed opacity-50"
                                        : ""
                                }`}
                            >
                                {loading ? <Loader /> : submitLabel}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BackupVerificationModal;
